import classNames from 'classnames';
import parseISO from 'date-fns/parseISO';
import { useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import {
    useCategoryUrlGenerator,
    useFormatUrlGenerator,
    useTopicUrlGenerator,
} from '../../hooks/useUrlGenerator';

import PillButton from '../buttons/PillButton';
import Author from './Author';

import styles from '../../styles/partials/document-metadata.module.css';

export interface DocumentMetadataProps extends DocumentMetadata {
    className?: string | null;
}

function DocumentMetadata({
    credits = null,
    categories = null,
    topics = null,
    formats = null,
    publishedAt = null,
    className = null,
}: DocumentMetadataProps) {
    const formatUrl = useFormatUrlGenerator();
    const categoryUrl = useCategoryUrlGenerator();
    const topicUrl = useTopicUrlGenerator();
    const items = useMemo(
        () => [
            ...(categories || []).map(({ label, ...category }) => ({
                label,
                url: categoryUrl(category),
            })),
            ...(formats || []).map(({ label, ...format }) => ({
                label,
                url: formatUrl(format),
            })),
            ...(topics || []).map(({ label, ...topic }) => ({
                label,
                url: topicUrl(topic),
            })),
        ],
        [categories, topics, formats],
    );
    const publishedAtObj = useMemo(
        () => (publishedAt !== null ? parseISO(publishedAt) : null),
        [publishedAt],
    );
    return (
        <div
            className={classNames([
                styles.container,
                'document-metadata',
                {
                    [className as string]: className !== null,
                },
            ])}
        >
            {credits !== null && credits.length > 0 ? (
                <div className={styles.credits}>
                    {credits.map(({ author, role }, index) => (
                        <Author
                            key={`author-${index}`}
                            {...author}
                            role={role}
                            withLink
                            className={styles.author}
                        />
                    ))}
                </div>
            ) : null}
            {publishedAtObj !== null ? (
                <div className={styles.date}>
                    <FormattedMessage
                        defaultMessage="<strong>Publié le</strong> {date}"
                        description="Document metadata published date"
                        values={{
                            date: (
                                <FormattedDate
                                    day="numeric"
                                    month="long"
                                    year="numeric"
                                    value={publishedAtObj}
                                />
                            ),
                        }}
                    />
                </div>
            ) : null}
            {items.length > 0 ? (
                <div className={styles.items}>
                    {items.map(({ url: itemUrl, label }) => (
                        <PillButton
                            key={`item-${itemUrl}`}
                            href={itemUrl}
                            compact
                            bordered
                            className={styles.button}
                        >
                            {label}
                        </PillButton>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export default DocumentMetadata;
